/* ============================================================
   TEE BOX — UI VERSION FLAG (client side)

   Resolves whether this session renders the v1 or v2 client.
   The server is the source of truth; this module is the client
   half — it caches the answer, applies it to the document, and
   makes it readable synchronously at boot.

   WHY THE CACHE MATTERS

   The login screen is pre-auth: there is no member identity yet,
   so the server cannot be asked who this is. Three resolution
   points, in order:

     1. Boot — read the cached value synchronously, before React
        mounts, and stamp `data-ui` on <html>. No network, no
        flash. This is the inline script in index.html, which
        duplicates `readCached()` on purpose: it has to run before
        any module loads.
     2. OTP request — the `POST /api/otp/login` response carries
        `uiVersion` for the identity just typed, so a first-time
        beta user switches at the OTP step rather than after login.
     3. Login / member profile — authoritative. Cached here.

   Net effect: a brand-new beta user sees the v1 login form once,
   then v2 from the OTP step onward. Everyone else never sees v2.

   NEVER FLIP MID-SESSION. Cart, booking draft and Redux state are
   shared between the trees; switching trees mid-flow strands the
   user. `applyUiVersion` is called at boot and at login, and
   `setUiVersion` defers a change to the next cold start unless
   explicitly told otherwise.
   ============================================================ */

import { isStandalone } from '@/utils/pwa';
import { isNativeWebView } from '@/utils/nativeBridge';
import { isDemoMode } from '@/demo/demoMode';

export type UiVersion = 'v1' | 'v2';

export const UI_VERSION_STORAGE_KEY = 'uiVersion';

const isUiVersion = (v: unknown): v is UiVersion => v === 'v1' || v === 'v2';

/** Read the cached version. Never throws — Safari private mode
 *  can make localStorage access itself raise. */
export const readCachedUiVersion = (): UiVersion => {
  try {
    const stored = window.localStorage.getItem(UI_VERSION_STORAGE_KEY);
    return isUiVersion(stored) ? stored : 'v1';
  } catch {
    return 'v1';
  }
};

/**
 * `?ui=v2` pins the version and persists it.
 */
const readOverride = (): UiVersion | null => {
  try {
    const q = new URLSearchParams(window.location.search).get('ui');
    return isUiVersion(q) ? q : null;
  } catch {
    return null;
  }
};


export const DESKTOP_QUERY = '(pointer: fine) and (min-width: 768px)';

export const isDesktop = (): boolean => {
  try {
    return window.matchMedia(DESKTOP_QUERY).matches;
  } catch {
    /* No matchMedia (SSR, jsdom without a stub) — fall back to the
       cache rather than guessing. */
    return false;
  }
};

const FORCE_V2_ON_DESKTOP =
  import.meta.env.VITE_FORCE_V2_ON_DESKTOP === 'true';

const isForcedV2 = (): boolean =>
  isDemoMode ||
  (!isNativeWebView() &&
  /* Desktop is decided by the flag alone — including a desktop-installed
     PWA, which is `isStandalone()` too and would otherwise slip through
     with the flag off. Non-desktop keeps the existing rule: the installed
     app gets v2. */
  (isDesktop() ? FORCE_V2_ON_DESKTOP : isStandalone()));

/** What this device should actually render, given a candidate. */
const effectiveVersion = (version: UiVersion): UiVersion =>
  readOverride() ?? (isForcedV2() ? 'v2' : version);

/** Stamp the version on <html>, which is what the CSS keys off. */
export const applyUiVersion = (version: UiVersion): void => {
  document.documentElement.setAttribute('data-ui', version);
};

/**
 * Persist a server-resolved version.
 *
 * `apply: false` (the default) writes the cache but leaves the
 * current session rendering whatever it started with — the
 * never-flip-mid-session rule. Pass `apply: true` only at a point
 * where no user state is in flight, i.e. immediately after login.
 */
export const setUiVersion = (
  version: UiVersion,
  { apply = false }: { apply?: boolean } = {},
): void => {
  try {
    window.localStorage.setItem(UI_VERSION_STORAGE_KEY, version);
  } catch {
    /* Storage unavailable — the session still works, it just
       re-resolves from the server next boot. */
  }
  if (apply) applyUiVersion(effectiveVersion(version));
};

/**
 * Resolve and apply at boot. Call once, as early as possible.
 * Returns the version in effect for this session.
 */
export const initUiVersion = (): UiVersion => {
  const override = readOverride();
  if (override) {
    if (isForcedV2()) {
      applyUiVersion(override);
      return override;
    }
    setUiVersion(override, { apply: true });
    return override;
  }

  /* Desktop and the installed app resolve ahead of the cache, and
     deliberately do not write to it — see `isForcedV2`. */
  if (isForcedV2()) {
    applyUiVersion('v2');
    return 'v2';
  }

  const version = readCachedUiVersion();
  applyUiVersion(version);
  return version;
};

/** The version in effect right now, per the document. */
export const currentUiVersion = (): UiVersion => {
  const attr = document.documentElement.getAttribute('data-ui');
  return isUiVersion(attr) ? attr : 'v1';
};
